/**
 * PassengerDatePickerField.js
 *
 * Self-contained date-picker field for the FlightBookingDetailsComponent
 * passenger form. Bundles the custom calendar header (month/year dropdowns),
 * masked dd/MM/yyyy text input, and the inline error message in one component.
 *
 * Usage:
 *   <PassengerDatePickerField
 *       selected={parseDateOnlyForPicker(formik.values.passgeners[i].dateOfBirth)}
 *       onChange={(date) => { resetSubmissionState(); formik.setFieldValue(`passgeners[${i}][dateOfBirth]`, formatDateOnlyForApi(date)); }}
 *       placeholderText="Date Of Birth*"
 *       maxDate={new Date()}
 *       errorText={hasSubmitted && formik?.errors?.passgeners?.[i]?.dateOfBirth}
 *   />
 */

import React from "react";
import DatePicker from "react-datepicker";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "../../../../helper/HwIcon";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const DATE_MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const DATE_YEAR_START = new Date().getFullYear() - 100;
const DATE_YEAR_END = new Date().getFullYear() + 15;
const DATE_MONTH_OPTIONS = DATE_MONTHS.map((month, index) => ({ label: month, value: index }));
const DATE_YEAR_OPTIONS = Array.from(
    { length: DATE_YEAR_END - DATE_YEAR_START + 1 },
    (_, i) => ({ label: String(DATE_YEAR_START + i), value: DATE_YEAR_START + i }),
);

// ─────────────────────────────────────────────────────────────────────────────
// Style constants
// ─────────────────────────────────────────────────────────────────────────────

const dpHeaderStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "6px 10px 10px",
};
const dpSelectsStyle = { display: "flex", gap: "8px", flex: 1, justifyContent: "center" };
const dpNavStyle = (disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    padding: 0,
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#334155",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.35 : 1,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
});
const dpNavIconStyle = { display: "inline-flex", transform: "scale(0.8)" };
const dpDropWrapStyle = (width) => ({ position: "relative", width });
const dpDropBtnStyle = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4px",
    padding: "7px 10px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "14px",
    fontWeight: 600,
    color: "#1e293b",
    cursor: "pointer",
    whiteSpace: "nowrap",
};
const dpDropChevronStyle = (open) => ({
    display: "inline-flex",
    color: "#64748b",
    transition: "transform 0.15s ease",
    transform: open ? "scale(0.6) rotate(180deg)" : "scale(0.6)",
});
const dpDropListStyle = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: "210px",
    overflowY: "auto",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.14)",
    zIndex: 5,
    padding: "5px",
};
const dpDropItemStyle = (active) => ({
    padding: "8px 10px",
    borderRadius: "7px",
    fontSize: "14px",
    cursor: "pointer",
    color: active ? "#fff" : "#1e293b",
    background: active ? "#2563eb" : "transparent",
    fontWeight: active ? 600 : 400,
});

// ─────────────────────────────────────────────────────────────────────────────
// Tooltip CSS injection (runs once per page load)
// ─────────────────────────────────────────────────────────────────────────────

if (typeof document !== "undefined" && !document.getElementById("hw-datefmt-tt-style")) {
    const hwDateFmtStyle = document.createElement("style");
    hwDateFmtStyle.id = "hw-datefmt-tt-style";
    hwDateFmtStyle.textContent = `
.hw-datefmt-tt{position:relative;display:block;}
.hw-datefmt-tt::after{content:attr(data-datefmt);position:absolute;bottom:calc(100% + 9px);left:12px;background:#fff;color:#000;font-size:12px;font-weight:500;line-height:1;padding:6px 9px;border-radius:3px;white-space:nowrap;opacity:0;visibility:hidden;transform:translateY(4px);transition:opacity .15s ease,transform .15s ease;pointer-events:none;z-index:30;box-shadow:0 4px 12px rgba(0,0,0,.18);}
.hw-datefmt-tt::before{content:"";position:absolute;bottom:calc(100% + 4px);left:22px;border:5px solid transparent;border-top-color:#283D6B;opacity:0;visibility:hidden;transition:opacity .15s ease;pointer-events:none;z-index:30;}
.hw-datefmt-tt:hover::after{opacity:1;visibility:visible;transform:translateY(0);}
.hw-datefmt-tt:hover::before{opacity:1;visibility:visible;}
`;
    document.head.appendChild(hwDateFmtStyle);
}

// ─────────────────────────────────────────────────────────────────────────────
// MaskedDateInput — formats numeric keypresses as dd/MM/yyyy with tooltip
// ─────────────────────────────────────────────────────────────────────────────

const MaskedDateInput = React.forwardRef(({ onChange, ...props }, ref) => {
    const handleChange = (event) => {
        const digits = String(event?.target?.value || "").replace(/\D/g, "").slice(0, 8);
        let formatted = digits;
        if (digits.length > 4) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        } else if (digits.length > 2) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        event.target.value = formatted;
        onChange(event);
    };
    const hasValue = !!String(props.value || "").trim();
    return (
        <span className={hasValue ? "" : "hw-datefmt-tt"} data-datefmt={hasValue ? undefined : "dd/mm/yyyy"}>
            <input ref={ref} inputMode="numeric" {...props} onChange={handleChange} />
        </span>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// CalendarDropdown — month/year dropdown with click-outside detection
// ─────────────────────────────────────────────────────────────────────────────

const CalendarDropdown = ({ value, options, onChange, width }) => {
    const [open, setOpen] = React.useState(false);
    const wrapRef = React.useRef(null);
    const listRef = React.useRef(null);
    React.useEffect(() => {
        const onDocClick = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);
    React.useEffect(() => {
        if (open && listRef.current) {
            const active = listRef.current.querySelector('[data-active="true"]');
            if (active && active.scrollIntoView) {
                active.scrollIntoView({ block: "center" });
            }
        }
    }, [open]);
    const selected = options.find((option) => option.value === value);
    return (
        <div ref={wrapRef} style={dpDropWrapStyle(width)}>
            <button type="button" style={dpDropBtnStyle} onClick={() => setOpen((prev) => !prev)}>
                <span>{selected ? selected.label : ""}</span>
                <span style={dpDropChevronStyle(open)}>
                    <ChevronDownIcon />
                </span>
            </button>
            {open ? (
                <div ref={listRef} style={dpDropListStyle}>
                    {options.map((option) => (
                        <div
                            key={option.value}
                            data-active={option.value === value ? "true" : "false"}
                            style={dpDropItemStyle(option.value === value)}
                            onClick={() => {
                                onChange(option.value);
                                setOpen(false);
                            }}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// monthOptionsForYear — filters month options to respect minDate/maxDate
// ─────────────────────────────────────────────────────────────────────────────

const monthOptionsForYear = (year, minDate, maxDate) =>
    DATE_MONTH_OPTIONS.filter((option) => {
        if (minDate && year === minDate.getFullYear() && option.value < minDate.getMonth()) {
            return false;
        }
        if (maxDate && year === maxDate.getFullYear() && option.value > maxDate.getMonth()) {
            return false;
        }
        return true;
    });

// ─────────────────────────────────────────────────────────────────────────────
// CalendarHeader — prev/next month nav + CalendarDropdown for month and year
// ─────────────────────────────────────────────────────────────────────────────

const CalendarHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    minDate,
    maxDate,
}) => {
    const minYear = minDate ? minDate.getFullYear() : DATE_YEAR_START;
    const maxYear = maxDate ? maxDate.getFullYear() : DATE_YEAR_END;
    const yearOptions = DATE_YEAR_OPTIONS.filter(
        (option) => option.value >= minYear && option.value <= maxYear,
    );
    const monthOptions = monthOptionsForYear(date.getFullYear(), minDate, maxDate);
    const handleYearChange = (nextYear) => {
        changeYear(nextYear);
        const allowed = monthOptionsForYear(nextYear, minDate, maxDate).map((option) => option.value);
        if (allowed.length && !allowed.includes(date.getMonth())) {
            changeMonth(date.getMonth() < allowed[0] ? allowed[0] : allowed[allowed.length - 1]);
        }
    };
    return (
        <div style={dpHeaderStyle}>
            <button
                type="button"
                style={dpNavStyle(prevMonthButtonDisabled)}
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                aria-label="Previous month"
            >
                <span style={dpNavIconStyle}><ChevronLeftIcon /></span>
            </button>
            <div style={dpSelectsStyle}>
                <CalendarDropdown
                    value={date.getMonth()}
                    options={monthOptions}
                    onChange={(next) => changeMonth(Number(next))}
                    width="130px"
                />
                <CalendarDropdown
                    value={date.getFullYear()}
                    options={yearOptions}
                    onChange={(next) => handleYearChange(Number(next))}
                    width="96px"
                />
            </div>
            <button
                type="button"
                style={dpNavStyle(nextMonthButtonDisabled)}
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                aria-label="Next month"
            >
                <span style={dpNavIconStyle}><ChevronRightIcon /></span>
            </button>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// buildCalendarProps — factory that returns standard DatePicker props
// ─────────────────────────────────────────────────────────────────────────────

export const buildCalendarProps = ({ minDate, maxDate } = {}) => ({
    minDate,
    maxDate,
    showPopperArrow: false,
    customInput: <MaskedDateInput />,
    renderCustomHeader: (params) => (
        <CalendarHeader {...params} minDate={minDate} maxDate={maxDate} />
    ),
});

// ─────────────────────────────────────────────────────────────────────────────
// PassengerDatePickerField — public component
// Wraps DatePicker + error message in one tidy unit.
//
// Props:
//   selected        {Date|null}   — controlled value (use parseDateOnlyForPicker)
//   onChange        {Function}    — called with the new Date (or null when cleared)
//   placeholderText {string}      — e.g. "Date Of Birth*"
//   maxDate         {Date}        — optional upper bound
//   minDate         {Date}        — optional lower bound
//   className       {string}      — extra classes for the <DatePicker> wrapper input
//   errorText       {string|false|null|undefined} — when truthy, rendered as error
// ─────────────────────────────────────────────────────────────────────────────

const PassengerDatePickerField = ({
    selected,
    onChange,
    placeholderText,
    maxDate,
    minDate,
    className,
    errorText,
}) => {
    return (
        <>
            <DatePicker
                {...buildCalendarProps({ maxDate, minDate })}
                selected={selected}
                onChange={onChange}
                shouldCloseOnSelect={true}
                preventOpenOnFocus
                onCalendarClose={() => {
                    try {
                        document.activeElement &&
                            document.activeElement.blur &&
                            document.activeElement.blur();
                    } catch (e) { }
                }}
                isClearable
                clearButtonClassName="hw--clear-button-date-picker"
                placeholderText={placeholderText}
                dateFormat="dd/MM/yyyy"
                className={className}
            />
            {errorText ? (
                <p className="hw-p-error-cls">{errorText}</p>
            ) : (
                ""
            )}
        </>
    );
};

export default PassengerDatePickerField;
